import React, { useEffect, useState } from "react";
import { FaSpinner, FaCheckCircle, FaChevronDown } from 'react-icons/fa';

// ===================================================================================
// A. Submission Card Skeleton Loader
// ===================================================================================
const SubmissionSkeleton = () => (
    <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-gray-800/50 border border-gray-700 rounded-lg p-5 animate-pulse">
                <div className="flex justify-between items-center">
                    <div className="space-y-2">
                        <div className="h-5 bg-gray-700 rounded w-48"></div>
                        <div className="h-4 bg-gray-700 rounded w-64"></div>
                    </div>
                    <div className="h-8 w-24 bg-gray-700 rounded-md"></div>
                </div>
            </div>
        ))}
    </div>
);

// ===================================================================================
// B. Main Contact Submissions Component
// ===================================================================================
function ContactSubmissionsComponent() { // Renamed slightly to avoid conflict before default export
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeIndex, setActiveIndex] = useState(null);
    const [replyMessage, setReplyMessage] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [feedback, setFeedback] = useState({ message: "", type: "" });

    useEffect(() => {
        fetch("https://voidapi.shigjeta.com/api/contact")
            .then((res) => res.json())
            .then((data) => {
                if (Array.isArray(data)) setSubmissions(data.sort((a,b) => new Date(b.createdAt) - new Date(a.createdAt)));
                else throw new Error("Invalid data format");
            })
            .catch((err) => {
                console.error("Error loading submissions:", err);
                setFeedback({ message: "Failed to load submissions.", type: "error" });
                setSubmissions([]);
            })
            .finally(() => setTimeout(() => setLoading(false), 500));
    }, []);

    const handleToggle = (index) => {
        if (activeIndex === index) {
            setActiveIndex(null);
        } else {
            setActiveIndex(index);
            setReplyMessage(""); // Reset reply message when opening a new one
            setFeedback({ message: "", type: "" });
        }
    };

    const handleReply = async (entry) => {
        setIsSubmitting(true);
        setFeedback({ message: "", type: "" });

        const payload = { email: entry.email, name: entry.name, subject: entry.subject || `Re: Your message to Void`, replyMessage };

        try {
            const res = await fetch("https://voidapi.shigjeta.com/api/contact/reply", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error("Server failed to send reply");

            setFeedback({ message: `Reply successfully sent to ${entry.email}`, type: "success" });
            setReplyMessage("");
        } catch (err) {
            setFeedback({ message: err.message || "An unexpected error occurred.", type: "error" });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) return <SubmissionSkeleton />;

    return (
        <div className="space-y-4">
            {submissions.length === 0 && !feedback.message ? (
                <div className="text-center py-10 text-gray-500">
                    <p>No contact submissions found.</p>
                </div>
            ) : (
                submissions.map((entry, i) => (
                    <div key={i} className="bg-gray-800/50 border border-gray-700 rounded-lg overflow-hidden transition-all duration-300">
                        {/* Card Header */}
                        <div className="p-5 flex flex-col sm:flex-row justify-between items-start sm:items-center cursor-pointer" onClick={() => handleToggle(i)}>
                            <div>
                                <p className="font-semibold text-white text-lg">{entry.name}</p>
                                <p className="text-sm text-gray-400">{entry.email}</p>
                                {entry.subject && <p className="text-sm text-cyan-400 mt-1 italic">Subject: {entry.subject}</p>}
                            </div>
                            <button className="flex items-center gap-2 mt-3 sm:mt-0 px-4 py-2 text-sm bg-gray-700 text-white rounded-md hover:bg-gray-600 transition-colors">
                                {activeIndex === i ? "Close" : "View & Reply"}
                                <FaChevronDown className={`transition-transform duration-300 ${activeIndex === i ? 'rotate-180' : ''}`} />
                            </button>
                        </div>

                        {/* Expandable Content */}
                        {activeIndex === i && (
                            <div className="px-5 pb-5 border-t border-gray-700">
                                <p className="text-gray-300 mt-4 mb-6 leading-relaxed whitespace-pre-wrap">{entry.message}</p>
                                
                                <div className="space-y-4">
                                    <textarea
                                        rows={5}
                                        value={replyMessage}
                                        onChange={(e) => setReplyMessage(e.target.value)}
                                        className="w-full bg-gray-900/50 border border-gray-600 rounded-md p-3 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-all"
                                        placeholder={`Write your reply to ${entry.name}...`}
                                    />

                                    {feedback.message && (
                                        <div className={`p-3 rounded-md text-sm font-semibold flex items-center gap-2 ${feedback.type === 'success' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                                            <FaCheckCircle /> {feedback.message}
                                        </div>
                                    )}

                                    <button
                                        onClick={() => handleReply(entry)}
                                        disabled={isSubmitting || !replyMessage}
                                        className="flex items-center justify-center px-5 py-2 bg-cyan-600 text-white font-semibold rounded-lg hover:bg-cyan-700 disabled:bg-gray-600 transition-colors"
                                    >
                                        {isSubmitting ? <FaSpinner className="animate-spin mr-2" /> : null}
                                        {isSubmitting ? "Sending..." : "Send Reply"}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                ))
            )}
        </div>
    );
};

// ===================================================================================
// C. The Corrected Default Export
// This is the line that fixes the error.
// ===================================================================================
export default ContactSubmissionsComponent;